import { USERID_ADDRESS_TYPE } from "@/constants/message";
import { resendMessage } from "@/features/chat/chatSlice";
import {
  selectCurrConversation,
  selectConversations,
} from "@/features/control/controlSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Conversation } from "@/types/conversation";
import OptionMenuWithError from "../OptionMenuWithError";
import "../messageStyles.css";

import { HardDriveDownload } from "lucide-react";
import { useCallback } from "react";
import LocationLink from "../LocationLink";
import ImagePreview from "./ImagePreview";
import { getMediaObjectUrl } from "@/utils/mediaCache";

interface SentImageMessageProps {
  id: number;
  created: string;
  time: string;
  image: {
    media: string;
    msg: string;
    fname: string;
    dname: string;
    time: string;
    lat?: string | null;
    lng?: string | null;
  };
  error?: boolean;
}

const SentImageMessage = ({
  id,
  created,
  time,
  image,
  error,
}: SentImageMessageProps) => {
  const dispatch = useAppDispatch();
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const conversations: Conversation[] = useAppSelector(selectConversations);
  // Cached blob URL so the base64 is decoded once, not re-decoded on every remount.
  const mediaUrl = getMediaObjectUrl(`s:${id}`, image.media);

  const handleResend = useCallback(async () => {
    const addressId = conversations[currConversationIndex!].addressId;
    const address = conversations[currConversationIndex!].address;
    const addressType = USERID_ADDRESS_TYPE;

    await dispatch(
      resendMessage({
        address,
        addressId,
        addressType,
        message: JSON.stringify(image),
      })
    );
  }, [dispatch, conversations, currConversationIndex]);

  return (
    <div key={id} className="message-container">
      <div className="message-time-container items-end">
        <span className="message-time">{created ? time : "now"}</span>
        <OptionMenuWithError error={error} handleResend={handleResend}>
          <div className="flex items-center justify-center gap-4 text-black">
            <a
              href={mediaUrl}
              download={image.dname}
              title="Download Image"
            >
              <HardDriveDownload />
            </a>
            <ImagePreview
              src={mediaUrl}
              alt={image.fname || "Message"}
              className="image-style"
            />
          </div>
        </OptionMenuWithError>
        {/* image caption */}
        {image.msg.length > 0 && (
          <OptionMenuWithError error={error}>
            <div className="message-text-container sent-color max-w-fit">
              <p className="attachment-message">{image.msg}</p>
            </div>
          </OptionMenuWithError>
        )}
        <LocationLink lat={image.lat} lng={image.lng} />
      </div>
    </div>
  );
};

export default SentImageMessage;
